import { useState, useCallback, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import filter from 'lodash/filter'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import AddIcon from '@mui/icons-material/Add'
import { useSnackbar } from 'notistack'
import Page from '../../../shared/components/layouts/Page'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import { useTeam } from '../../../contexts/TeamContext'
import { ShellSessionsProvider, useShellSessions } from '../../../contexts/ShellSessionsContext'
import { NodesProvider, useNodes } from '../../../contexts/NodesContext'
import { ProjectsProvider } from '../../../contexts/ProjectsContext'
import { ServicesProvider, useServices } from '../../../contexts/ServicesContext'
import { useAuthentication } from '../../../contexts/AuthenticationContext'
import useShellConnections from '../../../shared/hooks/useShellConnections'
import TerminalSessionList from './components/TerminalSessionList'
import SessionTerminal from './components/SessionTerminal'
import ConnectionPicker from './components/ConnectionPicker'
import fetchJSON from '../../../shared/resources/fetchJSON'
import { API_HOST_NAME } from '../../../shared/resources/api/v1/constants'
import { SHELL_CONNECTION_STATE } from '../../../shared/websocket/constants'

// Calculate terminal dimensions from container size
// Matches Terminal.jsx: fontSize 14, Menlo/Monaco monospace font
const calculateTerminalDimensions = (containerWidth, containerHeight) => {
  const charWidth = 8.4       // Approximate width for 14px monospace
  const charHeight = 17       // Approximate height for 14px monospace
  const padding = 8           // Terminal padding (MUI spacing 1 = 8px)
  const scrollbarWidth = 17   // Standard scrollbar width

  const cols = Math.max(80, Math.floor((containerWidth - padding * 2 - scrollbarWidth) / charWidth))
  const rows = Math.max(24, Math.floor((containerHeight - padding * 2) / charHeight))

  return { cols, rows }
}

function TerminalContent() {
  const { team } = useTeam()
  const { teamSlug } = useParams()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { getValidToken } = useAuthentication()
  const { nodes } = useNodes()
  const { services } = useServices()
  const {
    sessions,
    currentSessionId,
    currentSession,
    selectSession,
    addSession,
    addPendingSession,
    replacePendingSession,
    closeSession,
  } = useShellSessions()

  const [creating, setCreating] = useState(false)
  const terminalsRef = useRef(new Map())
  const terminalContainerRef = useRef(null)

  const {
    connect,
    disconnect,
    sendInput,
    sendResize,
    setTerminal,
    getConnectionState,
    getExitInfo,
    getError,
    isConnected,
  } = useShellConnections()

  // Get state for current session
  const connectionState = getConnectionState(currentSessionId)
  const exitInfo = getExitInfo(currentSessionId)
  const exitCode = exitInfo?.exitCode ?? null
  const error = getError(currentSessionId)
  const currentIsConnected = isConnected(currentSessionId)

  // Non-pending sessions that need a terminal instance
  const activeSessions = filter(sessions, (s) => !s.pending)

  // Store ref for a session terminal
  const setSessionTerminalRef = useCallback((sessionId, ref) => {
    if (ref) {
      terminalsRef.current.set(sessionId, ref)
    } else {
      terminalsRef.current.delete(sessionId)
    }
  }, [])

  // Re-fit terminal and focus when switching sessions
  useEffect(() => {
    if (currentSessionId) {
      const terminal = terminalsRef.current.get(currentSessionId)
      if (terminal) {
        if (terminal.fit) {
          setTimeout(() => {
            terminal.fit()
          }, 0)
        }
        if (currentIsConnected && terminal.focus) {
          terminal.focus()
        }
      }
    }
  }, [currentSessionId, currentIsConnected])

  // Auto-focus terminal when connected
  useEffect(() => {
    const terminal = terminalsRef.current.get(currentSessionId)
    if (terminal && currentIsConnected && terminal.focus) {
      terminal.focus()
    }
  }, [connectionState, currentIsConnected, currentSessionId])

  // Send resize to backend immediately after connection to sync PTY dimensions
  useEffect(() => {
    if (currentIsConnected && currentSessionId) {
      const terminalHandle = terminalsRef.current.get(currentSessionId)
      const terminal = terminalHandle?.getTerminal?.()
      if (terminal) {
        sendResize(currentSessionId, terminal.cols, terminal.rows)
      }
    }
  }, [currentIsConnected, currentSessionId, sendResize])

  const getTerminalDimensions = useCallback(() => {
    if (terminalContainerRef.current) {
      const { clientWidth, clientHeight } = terminalContainerRef.current
      return calculateTerminalDimensions(clientWidth, clientHeight)
    }
    return { cols: 120, rows: 30 }
  }, [])

  const createSession = useCallback((nodeId, dockerServiceId) => {
    const { cols, rows } = getTerminalDimensions()
    const url = `${API_HOST_NAME}/api/v1/admin/teams/${teamSlug}/nodes/${nodeId}/shell/sessions`
    const sessionData = {
      session: {
        cols,
        rows,
        shell_command: '/bin/bash --login -i',
      }
    }

    if (dockerServiceId) {
      sessionData.session.docker_service_id = dockerServiceId
    }

    return getValidToken()
      .then((token) => {
        const options = { headers: { Authorization: `Bearer ${token}` } }
        return fetchJSON(url, 'POST', options, sessionData)
      })
      .then((response) => {
        const session = response.shellSession || response.session || response
        if (!session || !session.id) {
          throw new Error('Invalid session response')
        }

        const node = nodes.find((n) => n.id === nodeId)
        session.nodeName = node?.name || node?.hostname

        if (dockerServiceId) {
          const service = services.find((s) => s.id === dockerServiceId)
          session.serviceName = service?.name
          session.dockerServiceId = dockerServiceId
        }

        return session
      })
  }, [teamSlug, getValidToken, getTerminalDimensions, nodes, services])

  const handleNewSession = useCallback(() => {
    addPendingSession({
      id: `pending-${Date.now()}`,
      nodeName: formatMessage({ id: 'terminal.pendingSession.name' }),
    })
  }, [addPendingSession, formatMessage])

  const handleConnect = useCallback((nodeId, dockerServiceId) => {
    setCreating(true)
    const pendingId = currentSession?.pending ? currentSessionId : null

    createSession(nodeId, dockerServiceId)
      .then((session) => {
        if (pendingId) {
          replacePendingSession(pendingId, session)
        } else {
          addSession(session)
        }
        return connect(session.id)
      })
      .catch(() => {
        enqueueSnackbar(
          formatMessage({ id: 'terminal.newSession.error' }),
          { variant: 'error' }
        )
      })
      .finally(() => {
        setCreating(false)
      })
  }, [currentSession, currentSessionId, createSession, replacePendingSession, addSession, connect, enqueueSnackbar, formatMessage])

  const handleSelectSession = useCallback((sessionId) => {
    if (sessionId !== currentSessionId) {
      selectSession(sessionId)
    }
  }, [currentSessionId, selectSession])

  const handleCloseSession = useCallback((sessionId) => {
    disconnect(sessionId)
    terminalsRef.current.delete(sessionId)
    closeSession(sessionId)
  }, [disconnect, closeSession])

  const getStatusChip = () => {
    switch (connectionState) {
      case SHELL_CONNECTION_STATE.CONNECTING:
        return <Chip label={<FormattedMessage id="terminal.status.connecting" />} color="warning" size="small" />
      case SHELL_CONNECTION_STATE.CONNECTED:
        return <Chip label={<FormattedMessage id="terminal.status.connected" />} color="success" size="small" />
      case SHELL_CONNECTION_STATE.ERROR:
        return <Chip label={<FormattedMessage id="terminal.status.error" />} color="error" size="small" />
      default:
        return <Chip label={<FormattedMessage id="terminal.status.disconnected" />} size="small" />
    }
  }

  const showTerminal = currentSession && !currentSession.pending

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="terminal.title" />}
        entities={[{ team }]}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewSession}
          >
            <FormattedMessage id="terminal.newSession.button" />
          </Button>
        }
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          width: "100%",
          flexDirection: { xs: 'column', md: 'row' },
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* Left sidebar - session list */}
        <Box
          sx={{
            width: { xs: '100%', md: 250 },
            flexShrink: 0,
            borderRight: { xs: 0, md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          <TerminalSessionList
            sessions={sessions}
            currentSessionId={currentSessionId}
            onSelect={handleSelectSession}
            onClose={handleCloseSession}
          />
        </Box>

        {/* Main content - terminal or connection picker */}
        <Box
          ref={terminalContainerRef}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {showTerminal ? (
            <>
              {/* Status bar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                {getStatusChip()}
                {exitCode !== null && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    <FormattedMessage
                      id="terminal.exit.message"
                      values={{ exitCode }}
                    />
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ py: 0 }}>
                    {error}
                  </Alert>
                )}
              </Box>

              {/* Terminals - one per session, show/hide with CSS */}
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {activeSessions.map((session) => (
                  <SessionTerminal
                    key={session.id}
                    ref={(ref) => setSessionTerminalRef(session.id, ref)}
                    sessionId={session.id}
                    sendInput={sendInput}
                    sendResize={sendResize}
                    setTerminal={setTerminal}
                    visible={session.id === currentSessionId}
                  />
                ))}
              </Box>
            </>
          ) : (
            <ConnectionPicker onConnect={handleConnect} disabled={creating} />
          )}
        </Box>
      </Box>
    </Page>
  )
}

function TerminalIndexPage() {
  return (
    <NodesProvider>
      <ProjectsProvider>
        <ServicesProvider>
          <ShellSessionsProvider>
            <TerminalContent />
          </ShellSessionsProvider>
        </ServicesProvider>
      </ProjectsProvider>
    </NodesProvider>
  )
}

export default TerminalIndexPage
